/**
 * Hook de **transición de estado** de una orden (mutación).
 *
 * Envuelve `transitionOrder` (complete/deliver/cancel/reactivate/delete) en un
 * `useMutation`. Tras éxito **invalida las queries afectadas**
 * (`docs/frontend_conventions.md`, Datos): todos los listados de órdenes (la orden
 * cambia de estado y debe salir/entrar de las listas) y el detalle de esa orden.
 * Una transición mueve la orden entre estados, así que se invalida la raíz
 * `['orders']` en vez de una lista concreta.
 *
 * El gating de permiso lo decide la UI antes de exponer el botón; aquí solo se
 * ejecuta y se propaga el `ApiError` (la pantalla muestra `{error_code,message}`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { transitionOrder } from "../api";
import type { OrderAction, OrderDetail } from "../types";
import { ORDERS_ROOT_KEY, orderDetailQueryKey } from "./queryKeys";

interface TransitionVariables {
  id: number;
  action: OrderAction;
}

export function useOrderTransition() {
  const queryClient = useQueryClient();
  const sedeId = getActiveSedeId();

  return useMutation<OrderDetail | null, Error, TransitionVariables>({
    mutationFn: ({ id, action }) => transitionOrder(id, action),
    onSuccess: (_data, { id }) => {
      // Listados: la orden cambió de estado, recargar todas las listas.
      void queryClient.invalidateQueries({ queryKey: ORDERS_ROOT_KEY });
      // Detalle de esta orden (si está abierto/cacheado).
      void queryClient.invalidateQueries({
        queryKey: orderDetailQueryKey(sedeId, id),
      });
    },
  });
}
